import React, { useEffect, useState } from "react";
import { navigation } from "@origin-digital/reporting-client";
import { Loading } from "src/components/Loading";
import { config } from "src/config";
import { links } from "src/consts";
import { isAuthenticated } from "src/utils/auth";
import {
  formatName,
  retrieveValidLocalStorageValue,
} from "../../utils/helpers";
import {
  IHeroContent,
  ISpikeContent,
  ITiledParagraphContent,
  Version,
} from "./types";
import { Spike as SpikeV1 } from "./sections/V1/Spike";
import { Spike as SpikeV2 } from "./sections/V2/Spike";
import { getContent } from "./content";

const REFERRER_NAME = "spikeReferrerName";
const REFERRER_ID = "spikeReferrerId";
const REFERRAL_DETAIL_TTL = 30 * 24 * 60 * 60 * 1000;
interface ISpike extends ISpikeContent {
  version: Version;
}

export const replaceName = ({
  content,
  name,
}: {
  content: IHeroContent;
  name: string;
}): IHeroContent => ({
  ...content,
  byline: Array.isArray(content.byline)
    ? content.byline.map((line) => line.replace("{name}", formatName(name)))
    : content.byline.replace("{name}", formatName(name)),
});

export const Spike: React.FC = () => {
  const { localStorage, history } = window;
  const { href, pathname, search } = window.location;
  const [loading, setLoading] = useState(true);
  const searchParams = new URLSearchParams(search);
  const referrerName =
    searchParams.get("n") || retrieveValidLocalStorageValue(REFERRER_NAME);
  const referrerId =
    searchParams.get("i") || retrieveValidLocalStorageValue(REFERRER_ID);
  const version =
    searchParams.get("v") === Version.V2 ? Version.V2 : Version.V1;
  const landingPageEnv = config.env === "prod" ? "prod" : "nonProd";
  const landingPageType = referrerName && referrerId ? "referral" : "standard";
  const [heroContent, cvpContent, switchContent, registerContent] = getContent(
    version
  );

  useEffect(() => {
    if (isAuthenticated() && pathname.includes("/signup")) {
      return window.location.assign(`${links.myAccountSpike}${search}`);
    }
    const redirect = `${pathname}?v=${version}`;
    if (referrerName && referrerId) {
      history.replaceState(undefined, "Origin Spike", redirect);
      localStorage.setItem(
        REFERRER_NAME,
        JSON.stringify({
          value: referrerName,
          TTL: Date.now() + REFERRAL_DETAIL_TTL,
        })
      );
      localStorage.setItem(
        REFERRER_ID,
        JSON.stringify({
          value: referrerId,
          TTL: Date.now() + REFERRAL_DETAIL_TTL,
        })
      );
    }

    navigation("screen", {
      currentUri: href,
      path: redirect,
    });

    if (referrerName && referrerId) {
      heroContent[landingPageEnv][
        landingPageType
      ].cta.primary.location = `${heroContent[landingPageEnv][landingPageType].cta.primary.location}${search}`;
      registerContent.cta[`${landingPageEnv}Location`] = `${
        registerContent.cta[`${landingPageEnv}Location`]
      }${search}`;
      (heroContent[landingPageEnv].referral as IHeroContent) = replaceName({
        content: heroContent[landingPageEnv].referral,
        name: referrerName,
      });
      switchContent.referral.sections = switchContent.referral.sections.map(
        (section) => ({
          ...section,
          textSection: section.textSection.map((text) =>
            text.replace("{name}", formatName(referrerName))
          ),
        })
      );
    }

    setLoading(false);
  }, []);

  if (loading) {
    return <Loading />;
  }

  return (
    <SpikePage
      version={version}
      heroContent={heroContent[landingPageEnv][landingPageType]}
      paragraphContent={cvpContent}
      tileParagraphContent={
        switchContent[landingPageType] as ITiledParagraphContent
      }
      registerContent={registerContent}
    />
  );
};

const SpikePage = (props: ISpike) => {
  return props.version === Version.V2 ? (
    <SpikeV2 {...props} />
  ) : (
    <SpikeV1 {...props} />
  );
};
